import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Hammer, Wifi, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import Reviews from "@/components/Reviews";
import { Reveal, HeroLine, Eyebrow, Marquee } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1755488254231-66910458a53c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxjYW5hZGElMjBsYWtlJTIwZm9yZXN0JTIwcGluZSUyMHN1bnNldHxlbnwwfHx8fDE3OTE2NzkwNDZ8MA&ixlib=rb-4.1.0&q=85";
const TOWNS = ["Bear Lake", "Burk's Falls", "Callander", "Dunchurch", "Emsdale", "Katrine", "Kearney", "Port Loring", "Magnetawan", "Novar", "Powassan", "Restoule", "South River", "Sprucedale", "Sundridge"];

const pillars = [
  { icon: Hammer, title: "Plainspoken & Honest", copy: "No confusing tech jargon, no hidden monthly traps. You'll always know what you're paying for and why." },
  { icon: Wifi, title: "Built to Last", copy: "Fast-loading sites that work on rural cell service and cottage wifi — because that's what your customers have." },
  { icon: MapPin, title: "Local & Accessible", copy: "Based right here in the Almaguin Highlands. Call, email, or meet for a coffee on Main Street." },
];

const services = [
  { name: "The Starter Workshop", price: "$349", tag: "A 1-page site to get you online" },
  { name: "The Main Street Standard", price: "$599", tag: "Up to 4 pages — right for most small businesses" },
  { name: "Business Plus", price: "$849", tag: "Up to 6 pages with testimonials & local SEO" },
  { name: "Custom Website", price: "From $1,000", tag: "Booking, integrations & special features" },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Good Work Web Co. | Websites for Almaguin Highlands"
        description="Good Work Web Co. builds affordable, professional websites for tradespeople and small businesses across the Almaguin Highlands in Northern Ontario."
      />

      {/* HERO */}
      <section ref={heroRef} className="grain relative min-h-[92vh] flex items-end overflow-hidden bg-[#1A2621]" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img
            src={HERO_IMG}
            alt="Sunset over a quiet Northern Ontario lake lined with pine forest"
            className="w-full h-[120%] object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#1A2621]/70" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#1A2621] to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-[2] w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 pt-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.6 }}>
            <span className="font-head font-bold uppercase tracking-[0.24em] text-xs sm:text-sm text-[#C58A47]" data-testid="hero-eyebrow">
              Almaguin Highlands · Northern Ontario
            </span>
          </motion.div>

          <h1 className="mt-4 font-head font-bold uppercase text-[#F3EFE5] leading-[0.92] tracking-tight text-[clamp(3rem,9vw,7.5rem)]" data-testid="hero-heading">
            <HeroLine delay={0.25}>You do <span className="text-[#C58A47]">good work.</span></HeroLine>
            <HeroLine delay={0.42}>Your website</HeroLine>
            <HeroLine delay={0.59}>should too.</HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-[#F3EFE5]/80 text-base sm:text-lg leading-relaxed"
            data-testid="hero-copy"
          >
            Affordable, professional websites for the hardworking people and small businesses that keep our communities going.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-quote-cta"
              className="group inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-7 py-4 transition-colors duration-200"
            >
              Get a Free Quote
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/work"
              data-testid="hero-work-cta"
              className="inline-flex items-center gap-2 border border-[#F3EFE5]/40 hover:border-[#F3EFE5] text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-7 py-4 transition-colors duration-200"
            >
              See Our Work
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <Marquee items={TOWNS} />

      {/* PILLARS */}
      <section className="py-12 sm:py-16 lg:py-20" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Why Good Work</Eyebrow>
            <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#233D35] mt-3 max-w-2xl leading-[0.95]">
              A web shop that talks like a neighbour.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12}>
                <div className="border-t-2 border-[#233D35] pt-6 group" data-testid={`pillar-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-head font-bold text-[#C58A47] text-lg">0{i + 1}</span>
                    <p.icon size={26} className="text-[#314F64] transition-transform duration-300 group-hover:-translate-y-1" />
                  </div>
                  <h3 className="font-head font-bold uppercase tracking-tight text-xl sm:text-2xl text-[#314F64] mt-4">{p.title}</h3>
                  <p className="mt-3 text-[#77786F] text-base leading-relaxed">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="bg-[#EBE5D8] py-12 sm:py-16 lg:py-20" data-testid="services-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Services & Pricing</Eyebrow>
              <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#233D35] mt-3 leading-[0.95]">
                Straightforward packages.<br />Honest prices.
              </h2>
            </div>
            <Link to="/services" data-testid="services-teaser-link" className="link-rule font-head font-bold uppercase tracking-[0.12em] text-sm text-[#233D35]">
              See full pricing
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.1}>
                <Link
                  to="/services"
                  data-testid={`service-card-${i}`}
                  className="block bg-white border border-[#E2DACB] p-5 sm:p-6 h-full shadow-[0_4px_20px_rgba(35,61,53,0.06)] transition-transform duration-300 hover:-translate-y-1.5"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-head font-bold uppercase tracking-tight text-lg sm:text-xl text-[#233D35] leading-tight">{s.name}</h3>
                    <span className="font-head font-bold text-xl text-[#C58A47] shrink-0">{s.price}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-[#77786F]">{s.tag}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="py-12 sm:py-16 lg:py-20" data-testid="featured-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Our Work</Eyebrow>
              <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#233D35] mt-3 leading-[0.95]">
                Built for folks around here.
              </h2>
            </div>
            <Link to="/work" data-testid="featured-work-link" className="link-rule font-head font-bold uppercase tracking-[0.12em] text-sm text-[#233D35]">
              View all work
            </Link>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
              <Link to="/work" className="group block relative overflow-hidden border border-[#E2DACB]" data-testid="featured-work-main">
                <img
                  src="/work/almaguin-yard-solutions.jpg"
                  alt="Almaguin Yard Solutions website — excavation and landscaping in cottage country"
                  className="w-full h-[320px] sm:h-[460px] object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-[#1A2621]/85 backdrop-blur-sm p-5 sm:p-6">
                  <span className="font-head font-bold uppercase tracking-[0.2em] text-xs text-[#C58A47]">Trades & Property Services</span>
                  <h3 className="font-head font-bold uppercase tracking-tight text-2xl text-[#F3EFE5] mt-1">Almaguin Yard Solutions</h3>
                </div>
              </Link>
            </Reveal>
            <div className="grid grid-rows-2 gap-6">
              {[
                { img: "/work/almaguin-chamber.jpg", alt: "Almaguin Highlands Chamber of Commerce website — misty lake and pines", cat: "Community & Non-Profit", title: "Almaguin Highlands Chamber of Commerce", id: "featured-work-chamber" },
                { img: "/work/kerby-leadership.jpg", alt: "Kerby Stivene Leadership website — developing leaders for what comes next", cat: "Professional Services", title: "Kerby Stivene Leadership", id: "featured-work-kerby" },
              ].map((w, i) => (
                <Reveal key={w.id} delay={0.12 + i * 0.1}>
                  <Link to="/work" className="group block relative overflow-hidden border border-[#E2DACB]" data-testid={w.id}>
                    <img src={w.img} alt={w.alt} className="w-full h-[200px] sm:h-[218px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-[#1A2621]/85 backdrop-blur-sm p-4 sm:p-5">
                      <span className="font-head font-bold uppercase tracking-[0.2em] text-[11px] text-[#C58A47]">{w.cat}</span>
                      <h3 className="font-head font-bold uppercase tracking-tight text-xl text-[#F3EFE5] mt-0.5">{w.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="mt-6">
            <p className="text-sm text-[#77786F]" data-testid="sample-note">
              Every project here is live and working — visit Our Work to click through them yourself.
            </p>
          </Reveal>
        </div>
      </section>

      <Reviews />

      {/* ABOUT TEASER */}
      <section className="bg-[#314F64] text-[#F3EFE5] grain" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative z-[2] grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <Eyebrow light>About Us</Eyebrow>
            <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[0.95]">
              A neighbour who builds websites.
            </h2>
            <p className="mt-5 text-[#F3EFE5]/80 text-base sm:text-lg leading-relaxed">
              Good Work Web Co. started with a simple observation: the people doing the hardest, most honest work in our communities often have the hardest time getting found online. We're here to fix that — with fair prices, plain language, and websites built like good tools: sturdy, useful, and made to last.
            </p>
            <Link
              to="/about"
              data-testid="about-teaser-link"
              className="group mt-7 inline-flex items-center gap-2 border border-[#F3EFE5]/40 hover:border-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-6 py-3.5 transition-colors duration-200"
            >
              Read our story
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#C58A47]" aria-hidden="true" />
              <img
                src="https://images.unsplash.com/photo-1659930087003-2d64e33181f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxjYXJwZW50ZXIlMjB3b29kd29ya2VyJTIwdHJhZGVzcGVyc29uJTIwd29ya2luZyUyMHdvcmtzaG9wfGVufDB8fHx8MTc5MTY3OTAzOXww&ixlib=rb-4.1.0&q=85"
                alt="Craftsman checking measurements on a piece of timber"
                className="relative w-full h-[320px] sm:h-[420px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
};

export default Home;
